import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import type { BillingsAttainment, BillingsTargetCategory } from '@/types'

/**
 * Target against actual, for the company or for one rep.
 *
 * It reports YEAR TO DATE, not the full year, per Jason's second review: "target
 * variance should count from the current point of the year". Completed months only.
 * Measured on his real FY2027 targets on 28 August, the full-year reading was 41.9%
 * and the year-to-date reading 90.2% - the first is arithmetically true and tells him
 * nothing, because five months cannot deliver twelve months of target.
 *
 * The month in progress is excluded on purpose. Counting a whole month's target against
 * a part-finished month makes the number dip at every month-start and recover by
 * month-end with nothing having changed; on 28 August that alone was six points.
 *
 * The full-year target rides along as muted context so pace against the annual
 * commitment stays visible, but it is not a second percentage competing for attention.
 *
 * Deliberately no month-by-month grid: those are already three cards further down, and
 * repeating them here would make the one thing Jason opens the page for - are we ahead
 * or behind - the hardest thing on it to find.
 *
 *
 * WHAT IS AND IS NOT SAID
 *
 * A category with no target shows "No target set" and nothing else. It does not show
 * 0, or 0%, or a variance of minus the whole actual, because none of those are true:
 * no target was set, so there is no shortfall to report. This is the same distinction
 * the API makes by returning null rather than 0 (see billingsTargetsService.variance).
 *
 * Where only some months carry a target, that is stated. An annual target built from
 * four months is not comparable to twelve months of actuals, and a reader who cannot
 * see the difference will read a false shortfall.
 *
 *
 * ACCESSIBILITY
 *
 * Variance is never carried by colour alone: every row spells out "above" or "below"
 * in text, and the sign is in the number. Colour is an additional cue only, which is
 * the same rule BillingsMatrix follows - a credit note, or a missed target, is exactly
 * the figure a colour-blind reader must not misread. Row headers carry scope so a
 * screen reader announces the category with each figure, and the table has a real
 * caption.
 */

const CATEGORY_LABELS: Record<BillingsTargetCategory, string> = {
  total: 'Overall',
  software: 'Software',
  service: 'Service',
}

const ORDER: BillingsTargetCategory[] = ['total', 'software', 'service']

const money = (n: number) => n.toLocaleString('en-CA', { maximumFractionDigits: 0 })

interface TargetVsActualProps {
  attainment: BillingsAttainment
  monthCount: number
  /** Omit to render read-only, which is what a rep viewing themselves will get. */
  onEdit?: () => void
  title?: string
  /**
   * What is on screen: "September 2025", "FY2026 Q3", "FY2026". The card no longer knows
   * it is looking at a year, because it usually is not.
   */
  periodLabel?: string
  /** True when the window is a single month, which changes what "to date" can mean. */
  isSingleMonth?: boolean
}

export function TargetVsActual({
  attainment,
  monthCount,
  onEdit,
  title = 'Target vs actual',
  periodLabel = 'this year',
  isSingleMonth = false,
}: TargetVsActualProps) {
  const rows = ORDER.map((category) => ({
    category,
    data: attainment.categories.find((c) => c.category === category),
  })).filter((r) => r.data)

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle>{title}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {/*
              A period still running is NOT "nothing to measure".
              
              This used to say "This year has not started yet" for any window with no
              completed month in it - which, once a month could be selected, was the
              default view for the whole of every month, on a page that usually has a
              target sitting right there. The month has started and there is something to
              measure; what is true is only that it is not finished.
            */}
            {attainment.months_completed === 0
              ? isSingleMonth
                ? `${periodLabel} is still in progress, so this is what has landed against the target so far, not a shortfall.`
                : `${periodLabel} has not started yet, so there is nothing to measure.`
              : `${periodLabel}, to date: ${attainment.months_completed} completed ${
                  attainment.months_completed === 1 ? 'month' : 'months'
                } of ${attainment.month_count}. The month in progress is not counted.`}
          </p>
        </div>
        {onEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            {attainment.any_target_set ? 'Edit targets' : 'Set targets'}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!attainment.any_target_set ? (
          <p className="text-sm text-muted-foreground">
            No targets have been set for {periodLabel} yet
            {onEdit ? ', so there is nothing to measure against.' : '.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">
                Target against actual billings for {periodLabel}, by product category
              </caption>
              <thead>
                <tr className="border-b">
                  <th scope="col" className="text-left font-medium py-2 pr-4">
                    Category
                  </th>
                  <th scope="col" className="text-right font-medium py-2 pr-4">
                    Target to date
                  </th>
                  <th scope="col" className="text-right font-medium py-2 pr-4">
                    Actual
                  </th>
                  <th scope="col" className="text-right font-medium py-2 pr-4">
                    Difference
                  </th>
                  <th scope="col" className="text-right font-medium py-2">
                    Of target
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ category, data }) => {
                  const a = data!.to_date
                  const fullYear = data!.annual
                  const partial =
                    a.target !== null &&
                    a.months_with_target > 0 &&
                    a.months_with_target < (attainment.months_completed || monthCount)

                  if (a.target === null) {
                    return (
                      <tr key={category} className="border-b last:border-0">
                        <th scope="row" className="text-left font-normal py-2 pr-4">
                          {CATEGORY_LABELS[category]}
                        </th>
                        <td className="py-2 pr-4 text-right text-muted-foreground">
                          No target set
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums">{money(a.actual)}</td>
                        <td className="py-2 pr-4 text-right text-muted-foreground">—</td>
                        <td className="py-2 text-right text-muted-foreground">—</td>
                      </tr>
                    )
                  }

                  const above = (a.variance ?? 0) >= 0
                  return (
                    <tr key={category} className="border-b last:border-0">
                      <th scope="row" className="text-left font-normal py-2 pr-4">
                        {CATEGORY_LABELS[category]}
                        {partial && (
                          <span className="block text-xs text-muted-foreground">
                            {a.months_with_target} of {attainment.months_completed} completed months
                            have a target
                          </span>
                        )}
                        {fullYear.target !== null && (
                          <span className="block text-xs text-muted-foreground">
                            {periodLabel} {money(fullYear.target)}
                          </span>
                        )}
                      </th>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(a.target)}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{money(a.actual)}</td>
                      {/* The word carries the meaning; the colour only reinforces it.
                          `text-destructive` and no class for the good case, matching
                          BillingsMatrix.cellClass rather than introducing a raw palette
                          colour this module does not otherwise use. */}
                      <td
                        className={`py-2 pr-4 text-right tabular-nums ${
                          above ? 'font-medium' : 'text-destructive'
                        }`}
                      >
                        {above ? '+' : '−'}
                        {money(Math.abs(a.variance ?? 0))}{' '}
                        <span className="text-xs">{above ? 'above' : 'below'}</span>
                      </td>
                      <td className="py-2 text-right tabular-nums">
                        {a.percent === null ? '—' : `${money(a.percent)}%`}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
